/**
 * Money arithmetic on the decimal strings the API sends.
 *
 * The backend uses Python's Decimal precisely so invoice totals cannot drift.
 * Parsing those strings into JS floats to add them up would put the drift
 * straight back: 0.1 + 0.2 is not 0.3 in binary floating point, and an invoice
 * footer that disagrees with the invoice by a cent is a support conversation.
 * So sum in integer cents and format back to a string.
 */

/** Split a decimal string into signed integer cents. */
export function toCents(value: string): number {
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const [whole = '0', frac = ''] = trimmed.replace(/^[-+]/, '').split('.');
  const cents = Number(whole || '0') * 100 + Number(frac.padEnd(2, '0').slice(0, 2) || '0');
  return negative ? -cents : cents;
}

export function fromCents(cents: number): string {
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const body = `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
  return negative ? `-${body}` : body;
}

export function sumMoney(values: string[]): string {
  return fromCents(values.reduce((acc, value) => acc + toCents(value), 0));
}

/** Logged seconds as "2h 05m" / "45m". */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours ? `${hours}h ${String(minutes).padStart(2, '0')}m` : `${minutes}m`;
}
